import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { canUseApp, useLogout, useMe } from '../hooks/useMe';
import { useFeedView } from '../hooks/useFeedView';
import { useTabTitle } from '../hooks/useTabTitle';
import { LoginPage, PendingPage } from '../pages/Login';
import { FeedBadge, ViewOnlyBadge } from './Badges';
import { Empty } from './Panel';
import { SignalToasts } from './SignalPanel';

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/signals', label: 'สัญญาณ Stochastic' },
  { to: '/sr', label: 'สัญญาณแนวรับแนวต้าน' },
  { to: '/sto-h4', label: 'Sto-H4' },
  { to: '/news', label: 'News' },
  { to: '/system', label: 'System' },
  { to: '/settings', label: 'Settings' },
];

function UserMenu() {
  const me = useMe();
  const logout = useLogout();
  const user = me.data?.user;
  if (!me.data?.authRequired || !user) return null;
  return (
    <span className="row" style={{ gap: 6 }}>
      {user.pictureUrl ? <img src={user.pictureUrl} alt="" width={24} height={24} className="avatar" /> : null}
      <span className="small">{user.displayName}</span>
      <button type="button" className="btn" onClick={() => logout.mutate()} disabled={logout.isPending}>
        ออกจากระบบ
      </button>
    </span>
  );
}

/** Login gate: with AUTH_REQUIRED only approved users see the app; others get the login or waiting page. */
function Gate() {
  const me = useMe();
  const { pathname } = useLocation();
  if (me.isLoading) return <Empty title="Loading…" />;
  if (me.isError || !me.data) return <Empty title="The MagicTradie! backend is unreachable." />;
  const { authRequired, user } = me.data;
  if (authRequired && !user) return <LoginPage me={me.data} />;
  if (authRequired && user && user.status !== 'approved') return <PendingPage user={user} />;
  if (pathname === '/login') return <Navigate to="/" replace />;
  return <Outlet />;
}

export function AppShell() {
  const view = useFeedView();
  const me = useMe();
  const allowed = canUseApp(me.data);
  useTabTitle(view, allowed);
  const isAdmin = me.data?.authRequired && me.data.user?.role === 'admin' && me.data.user.status === 'approved';
  const canSeeGuide = me.data?.user?.role === 'admin' && me.data.user.status === 'approved';
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="masthead">
        <div className="masthead__row">
          <NavLink to="/" className="brand" aria-label="MagicTradie! home">
            <span className="brand__mark">
              Magic<em>Tradie!</em>
            </span>
            <span className="brand__sub">XAUUSD intelligence</span>
          </NavLink>
          {allowed ? (
            <nav className="nav" aria-label="Primary">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}>
                  {n.label}
                </NavLink>
              ))}
              {canSeeGuide ? <NavLink to="/admin/guide">Guide</NavLink> : null}
              {isAdmin ? <NavLink to="/admin/users">Users</NavLink> : null}
            </nav>
          ) : null}
          <div className="masthead__status">
            {allowed ? <FeedBadge view={view} /> : null}
            <ViewOnlyBadge />
            <UserMenu />
          </div>
        </div>
      </header>
      <main id="main" className="page" tabIndex={-1}>
        <Gate />
      </main>
      {allowed ? <SignalToasts /> : null}
      <footer className="footer">
        Market data, news and BUY/SELL signal alerts — no orders are placed and there is no trading of any kind. Nothing
        here is financial advice.
      </footer>
    </div>
  );
}
